import React from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { CheckIcon } from 'lucide-react';
export function HeroSection() {
  return <>
      <section className="relative pt-12 pb-20 px-4 md:pt-20 md:pb-32 overflow-hidden bg-[#FDF8F0]" data-id="element-30">
        {/* Organic background decoration */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#E8EDE6] rounded-full blur-3xl opacity-50 -translate-y-1/2 translate-x-1/2" data-id="element-31" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#E8EDE6] rounded-full blur-3xl opacity-50 translate-y-1/2 -translate-x-1/2" data-id="element-32" />

        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center relative z-10" data-id="element-33">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} data-id="element-34">
            <span className="inline-block px-4 py-1 mb-6 text-sm font-medium tracking-wider text-[#2D3B2D] uppercase bg-[#E8EDE6] rounded-full" data-id="element-35">
              Método de 30 Días
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-[#2D3B2D] leading-tight mb-6" data-id="element-36">
              Plan Antipánico de{' '}
              <span className="text-[#C4A962] italic" data-id="element-37">Deudas</span>
            </h1>
            <p className="text-lg md:text-xl text-[#4A5D4A] mb-6 leading-relaxed" data-id="element-38">
              El sistema simple para pasar del caos y la vergüenza a un plan
              claro que se sostiene incluso cuando estás cansada.
            </p>

            {/* Value bullets */}
            <div className="mb-8 space-y-3" data-id="element-39">
              <p className="text-sm font-semibold text-[#2D3B2D] uppercase tracking-wide" data-id="element-40">
                En este plan vas a lograr:
              </p>
              <div className="flex items-start gap-2.5" data-id="element-41">
                <CheckIcon className="w-4 h-4 text-[#C4A962] mt-0.5 flex-shrink-0" data-id="element-42" />
                <span className="text-[#4A5D4A] text-sm leading-relaxed" data-id="element-43">
                  Saber qué pagar y en qué orden (sin improvisar cada mes)
                </span>
              </div>
              <div className="flex items-start gap-2.5" data-id="element-44">
                <CheckIcon className="w-4 h-4 text-[#C4A962] mt-0.5 flex-shrink-0" data-id="element-45" />
                <span className="text-[#4A5D4A] text-sm leading-relaxed" data-id="element-46">
                  Detectar fugas que te quitan dinero en automático
                </span>
              </div>
              <div className="flex items-start gap-2.5" data-id="element-47">
                <CheckIcon className="w-4 h-4 text-[#C4A962] mt-0.5 flex-shrink-0" data-id="element-48" />
                <span className="text-[#4A5D4A] text-sm leading-relaxed" data-id="element-49">
                  Crear una protección mínima para que un imprevisto no te
                  regrese a la tarjeta
                </span>
              </div>
            </div>

            <Button icon onClick={() => document.getElementById('pricing')?.scrollIntoView({
            behavior: 'smooth'
          })} data-id="element-50">
              Sí, quiero el Plan Antipánico
            </Button>

            {/* Microtexto */}
            <p className="mt-4 text-xs text-[#4A5D4A]/70 max-w-sm" data-id="element-51">
              PDF + Plantilla Google Sheets + Checklists · Acceso inmediato · En
              español · Compra única (sin suscripción)
            </p>
            <p className="mt-2 text-xs text-[#2D3B2D]/60 italic max-w-sm" data-id="element-52">
              No necesitas Excel perfecto, ni fuerza de voluntad, ni "empezar el
              lunes".
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative" data-id="element-53">
            <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl border-4 border-white/50" data-id="element-54">
              <img src="https://cdn.magicpatterns.com/uploads/va1Y99w6bnSmWnubZgxFoD/mockup1.png" alt="Plan Antipánico de Deudas Mockup" className="w-full h-auto object-cover" data-id="element-55" />
            </div>
            {/* Decorative element behind image */}
            <div className="absolute -bottom-6 -right-6 w-full h-full border-2 border-[#C4A962]/30 rounded-2xl -z-10" data-id="element-56" />
          </motion.div>
        </div>
      </section>

      {/* Opening Lead - The Emotional Hook */}
      <section className="py-20 px-4 bg-[#FAFAF7]" data-id="element-57">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="max-w-2xl mx-auto text-center md:text-lg leading-relaxed text-[#2D3B2D]" data-id="element-58">
          <p className="font-serif text-2xl md:text-3xl mb-8 text-[#2D3B2D]" data-id="element-59">
            Hay un momento que no le cuentas a nadie.
          </p>
          <div className="space-y-6 text-[#4A5D4A]" data-id="element-60">
            <p data-id="element-61">Abres tu app del banco, ves el saldo… y el pecho se aprieta.</p>
            <p data-id="element-62">
              No porque "no sepas sumar". Sino porque estás cansada de sentir
              que haces pagos… y no cambia nada.
            </p>
            <p data-id="element-63">
              Y entonces pasa lo de siempre: cierras la app, te dices "mañana lo
              arreglo", y sigues con el día como si no estuviera ahí.
            </p>
            <div className="py-4" data-id="element-64">
              <hr className="w-16 mx-auto border-[#C4A962]" data-id="element-65" />
            </div>
            <p className="font-medium text-xl text-[#2D3B2D]" data-id="element-66">
              Este plan existe para romper ese ciclo con algo que casi nadie te
              da: un sistema que no depende de tu fuerza de voluntad.
            </p>
          </div>
        </motion.div>
      </section>
    </>;
}